import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { UpgradeDialogSource } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import MobilePlanCardAutopilot from "../Plan/MobilePlanCard/Autopilot";
import MobilePlanCardPro from "../Plan/MobilePlanCard/Pro";
import MobilePlanCardVirtualAssistant from "../Plan/MobilePlanCard/VirtualAssistant";
import AutopilotPlanCard from "../Plan/PlanCard/Autopilot";
import FreePlanCard from "../Plan/PlanCard/Free";
import ProPlanCard from "../Plan/PlanCard/Pro";
import VirtualAssistantPlanCard from "../Plan/PlanCard/VirtualAssistant";
import { getAllPlans } from "../Plan/helpers";
import styles from "./styles";

export default function Subscription({
  upgradeDialogSource,
  plansOverride,
}: {
  upgradeDialogSource: UpgradeDialogSource | null;
  plansOverride?: Plans[];
}) {
  const theme = useTheme();
  const { discoverTab } = useContext(DiscoverViewContext);
  const isMediumScreen = useMediaQuery("(max-width:1300px)");
  const isLargeScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobileScreen =
    useMediaQuery(theme.breakpoints.down("md")) ||
    (isLargeScreen && discoverTab !== null);
  const { plans, tab, isAtEmailCreditCap } = useContext(SubscriptionContext);
  const { currentOrg } = useContext(OrganizationUserContext);

  const plansToDisplay =
    plansOverride ??
    getAllPlans(tab, upgradeDialogSource, currentOrg?.showAutopilotPlan);

  const getGrid = () => {
    const totalPlans = plansToDisplay?.length;
    const columnByPlan = 12 / (totalPlans || 1);
    if (isMobileScreen) return 12;
    if (discoverTab !== null || isMediumScreen) {
      return Math.max(6, columnByPlan);
    }
    if (isLargeScreen) return Math.max(4, columnByPlan);
    return columnByPlan;
  };

  const renderPlan = (plan: Plans) => {
    if (plan === Plans.FREE) {
      return (
        <Grid key={plan} item xs={12} md={getGrid()} sx={styles.planGridItem}>
          <FreePlanCard plan={plan} />
        </Grid>
      );
    } else if (plan === Plans.PRO || plan === Plans.PRO_YEARLY) {
      if (isMobileScreen && upgradeDialogSource) {
        return (
          <Grid item xs={12}>
            <MobilePlanCardPro />
          </Grid>
        );
      }
      return (
        <Grid key={plan} item xs={12} md={getGrid()} sx={styles.planGridItem}>
          <ProPlanCard plan={plan} />
        </Grid>
      );
    } else if (plan === Plans.VA || plan === Plans.VA_YEARLY) {
      if (isMobileScreen && upgradeDialogSource) {
        return (
          <Grid item xs={12}>
            <MobilePlanCardVirtualAssistant />
          </Grid>
        );
      }
      return (
        <Grid key={plan} item xs={12} md={getGrid()} sx={styles.planGridItem}>
          <VirtualAssistantPlanCard plan={plan} />
        </Grid>
      );
    } else if (plan === Plans.AUTOPILOT || plan === Plans.AUTOPILOT_YEARLY) {
      if (isMobileScreen && upgradeDialogSource) {
        return (
          <Grid item xs={12}>
            <MobilePlanCardAutopilot />
          </Grid>
        );
      }
      return (
        <Grid key={plan} item xs={12} md={getGrid()} sx={styles.planGridItem}>
          <AutopilotPlanCard plan={plan} />
        </Grid>
      );
    } else {
      return <></>;
    }
  };

  return plans ? (
    <>
      <Grid container spacing={1}>
        {plansToDisplay?.map((plan) => renderPlan(plan as Plans))}
      </Grid>
      {upgradeDialogSource && !isAtEmailCreditCap && (
        <Box sx={styles.rolloverSteps}>
          <Typography variant="h6" sx={styles.rolloverStepsTitle}>
            How Credit Rollover Works
          </Typography>
          <Grid container spacing={2}>
            {[
              {
                n: "1",
                title: "Send emails each month",
                desc: "Use your monthly email allotment to pitch brands as usual.",
              },
              {
                n: "2",
                title: "Unused credits carry forward",
                desc: "At the end of your billing cycle, any unused emails automatically roll over to next month.",
              },
              {
                n: "3",
                title: "Build up your sending power",
                desc: "Build up to 1,500 total email credits per month with rollover on top of your monthly allotment.",
              },
            ].map((step) => (
              <Grid item xs={12} md={4} key={step.n}>
                <Box sx={styles.rolloverStepCard}>
                  <Typography sx={styles.rolloverStepNumber}>
                    {step.n}
                  </Typography>
                  <Typography sx={styles.rolloverStepTitle}>
                    {step.title}
                  </Typography>
                  <Typography sx={styles.rolloverStepDesc}>
                    {step.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </>
  ) : (
    <></>
  );
}
